// ─────────────────────────────────────────────────────────────────────────────
// deltaCompanies.ts — WHAT EACH SALES TAB CAN BE OPENED ON, AND THE SCRIPT.
//
// `GET /api/delta/companies` · `GET /api/delta/script`. Forge owns both; this is
// the client and it degrades by name until they land.
//
// ── THE LIST IS WHAT HAS BEEN DONE, NOT WHAT COULD BE DONE ─────────────────
// `CompanySelector` answers a different question — Buck's qualified handoff, the
// companies Delta MAY work. This answers which ones he HAS worked, per tab, and
// a company appears on a tab the moment that tab's work exists for it:
//
//   research  a Research State record        calls  a stored Cold-call Script
//   pitch     a stored deck
//
// ── A TAB WITH NOTHING IS SERVED, NOT OMITTED ──────────────────────────────
// `pitch` has no producer today, so it arrives `companies: []` WITH ITS REASON.
// An absent dropdown and an empty one are different facts and the owner acts
// differently on each; `reason` is the producer's sentence and is never composed
// here. `unread` is a state WE are in about the list, not a state the list is in.
//
// ── NEITHER FILE EVER RENDERS `evidence` ───────────────────────────────────
// A script value is either a plain string or `{text, evidence}`. `words()` reads
// the text of both. The `evidence` string is an internal address and §10.14
// forbids speaking one: it is carried so a panel could mark a line as sourced,
// and it must never reach the screen as text.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

export const DELTA_COMPANIES_PATH = '/delta/companies';
export const DELTA_SCRIPT_PATH = '/delta/script';

/** The tabs the producer serves, in its own order. */
export type DeltaTabKey = 'research' | 'calls' | 'pitch';
export const DELTA_TABS: DeltaTabKey[] = ['research', 'calls', 'pitch'];

export interface DeltaCompany {
  companyId: string;
  business: string;
  /** Present on the research tab; absent elsewhere rather than invented. */
  researchState: string | null;
  checkedAt: string | null;
  /** The producer's Fit flag ("Fit: Strong") — published on the Calls tab only
   *  (owner ruling 2026-10-06); null where the tab or the composition has none. */
  fit?: string | null;
}

export interface DeltaTab {
  key: DeltaTabKey;
  title: string;
  companies: DeltaCompany[];
  /** The producer's sentence when a tab has nothing. Never written here. */
  reason: string | null;
}

export type ListPhase = 'unread' | 'absent' | 'read';

export interface DeltaTabs {
  phase: ListPhase;
  tabs: Record<DeltaTabKey, DeltaTab>;
  /** The query parameter the producer names for a selection. Read, not assumed. */
  selectWith: string;
  note: string;
}

const EMPTY = (key: DeltaTabKey, reason: string | null): DeltaTab =>
  ({ key, title: '', companies: [], reason });

export const TABS_UNREAD: DeltaTabs = {
  phase: 'unread',
  tabs: {
    research: EMPTY('research', null), calls: EMPTY('calls', null), pitch: EMPTY('pitch', null),
  },
  selectWith: 'company',
  note: '',
};

function company(row: any): DeltaCompany | null {
  const id = String((row && row.company_id) || '').trim();
  if (!id) return null;
  return {
    companyId: id,
    business: String((row && row.business) || '').trim() || id,
    // READ BY KEY. A tab that does not publish a state has none; a tab that
    // publishes one that is empty is a different thing, and neither becomes ''.
    researchState: row && 'research_state' in row && row.research_state != null
      ? String(row.research_state) : null,
    checkedAt: row && row.checked_at != null ? String(row.checked_at) : null,
    fit: row && Object.prototype.hasOwnProperty.call(row, 'fit') && typeof row.fit === 'string'
      && row.fit ? row.fit : null,
  };
}

export function normalizeDeltaTabs(body: any): DeltaTabs {
  if (!body || body.success !== true) return TABS_UNREAD;
  // `tabs: null` IS THE PRODUCER SAYING THE BOARD HAS NOT COMPOSED. That is not
  // an empty list of companies and must not paint as one.
  if (body.tabs == null) {
    return { ...TABS_UNREAD, phase: 'absent', note: String(body.reason || '') };
  }
  const out: Record<string, DeltaTab> = {};
  for (const key of DELTA_TABS) {
    const raw = body.tabs[key];
    out[key] = raw
      ? {
          key,
          title: String(raw.title || ''),
          companies: (Array.isArray(raw.companies) ? raw.companies : [])
            .map(company).filter(Boolean) as DeltaCompany[],
          reason: raw.reason != null ? String(raw.reason) : null,
        }
      // A TAB THE PRODUCER DID NOT SEND IS NOT A TAB WITH NOTHING IN IT.
      : EMPTY(key, 'this build does not publish a list for this tab');
  }
  return {
    phase: 'read',
    tabs: out as Record<DeltaTabKey, DeltaTab>,
    selectWith: String(body.select_with || 'company'),
    note: '',
  };
}

export async function readCompanies(): Promise<DeltaTabs> {
  let res: Response;
  try {
    res = await fetch(apiBase() + DELTA_COMPANIES_PATH, {
      headers: { Authorization: 'Bearer ' + authToken() },
    });
  } catch {
    return TABS_UNREAD;
  }
  if (res.status === 404) {
    return { ...TABS_UNREAD, phase: 'absent', note: 'this build does not publish the sales lists' };
  }
  if (!res.ok) return TABS_UNREAD;
  try {
    return normalizeDeltaTabs(await res.json());
  } catch {
    return TABS_UNREAD;
  }
}

// ── THE SCRIPT ─────────────────────────────────────────────────────────────

/** A spoken value: a plain string, or a sourced one. Both are normal. */
export type Spoken = string | { text?: string; evidence?: string };

export interface ScriptLine { line_id?: string; say?: Spoken }
export interface ScriptBranch {
  branch_id?: string; objection_class?: string; trigger?: Spoken;
  trigger_line?: string; returns_to?: string; panel_ref?: string;
}
export interface ScriptMove { branch_id?: string; move?: string; say?: Spoken }

export interface CallScriptTree {
  script_version?: string;
  lines?: ScriptLine[];
  questions?: Spoken[];
  listen_for?: Spoken[];
  consultation_ask?: Spoken;
  branches?: ScriptBranch[];
  branch_moves?: ScriptMove[];
}

export type ScriptPhase = 'unread' | 'none' | 'absent' | 'read';

export interface ScriptState {
  phase: ScriptPhase;
  companyId: string | null;
  script: CallScriptTree | null;
  /** The producer's own sentence — "no script written yet", or its refusal. */
  note: string;
}

export const SCRIPT_UNREAD: ScriptState =
  { phase: 'unread', companyId: null, script: null, note: '' };

/** THE WORDS OF A SPOKEN VALUE, WHICHEVER SHAPE IT IS IN. */
export function words(value: Spoken | undefined | null): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return String(value.text || '');
}

/** Whether a value carries a citation. TRUE/FALSE ONLY — the citation itself is
 *  an internal address and never leaves this module as text. */
export function sourced(value: Spoken | undefined | null): boolean {
  return Boolean(value && typeof value === 'object' && value.evidence);
}

/** The moves of one branch, in the order the producer wrote them. */
export function movesOf(script: CallScriptTree | null, branchId: string | undefined): ScriptMove[] {
  if (!script || !branchId) return [];
  return (script.branch_moves || []).filter((m) => m && m.branch_id === branchId);
}

/** The branches triggered at one line. A line with none renders none. */
export function branchesAt(script: CallScriptTree | null, lineId: string | undefined): ScriptBranch[] {
  if (!script || !lineId) return [];
  return (script.branches || []).filter((b) => b && b.trigger_line === lineId);
}

export async function readScript(companyId: string | null): Promise<ScriptState> {
  const id = String(companyId || '').trim();
  // NO SELECTION IS NOT AN ABSENT SCRIPT. The route refuses it by name and the
  // panel says "select a company" — telling the owner the work is missing when
  // it is the selection that is missing sends him looking for a script that
  // exists.
  if (!id) return { phase: 'none', companyId: null, script: null, note: '' };
  let res: Response;
  try {
    res = await fetch(apiBase() + DELTA_SCRIPT_PATH + '?company=' + encodeURIComponent(id), {
      headers: { Authorization: 'Bearer ' + authToken() },
    });
  } catch {
    return { ...SCRIPT_UNREAD, companyId: id };
  }
  if (res.status === 404) {
    return { phase: 'absent', companyId: id, script: null,
             note: 'this build does not publish the script' };
  }
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    return { ...SCRIPT_UNREAD, companyId: id };
  }
  if (res.status === 409) {
    return { phase: 'none', companyId: id, script: null, note: String(body?.error || '') };
  }
  if (!res.ok || !body || body.success !== true) return { ...SCRIPT_UNREAD, companyId: id };
  if (!body.script) {
    return { phase: 'read', companyId: id, script: null, note: String(body.reason || '') };
  }
  return { phase: 'read', companyId: id, script: body.script as CallScriptTree, note: '' };
}
